import { useState } from 'react';

import {
  Box,
  Table,
  Button,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
  Typography,
} from '@mui/material';

import { AuthLoader } from 'src/pages/auth/components/shared/AuthLoader';

import { ConfirmDialog } from 'src/components/custom-dialog';

import { formatPrice } from '../../utils/format-price';

type AddBalanceDialogProps = {
  isOpen?: boolean;
  onConfirmHandler?: () => void;
  onCloseHandler?: () => void;
  t: (key: string, options?: any) => string;
  isLoading?: boolean;
  isEstimateLoading?: boolean;
  estimateItem?: any;
  allCoupons?: any[];
  runCheckout?: (amount: number, coupon?: any) => Promise<void>;
  isSuperAdmin?: boolean;
};

export const AddBalanceDialog = ({
  isOpen = false,
  onConfirmHandler = () => {},
  onCloseHandler = () => {},
  t,
  isLoading,
  isEstimateLoading,
  estimateItem,
  allCoupons = [],
  runCheckout,
  isSuperAdmin,
}: AddBalanceDialogProps) => {
  const [selectedCoupon, setSelectedCoupon] = useState<any>(null);
  const [isApplying, setIsApplying] = useState(false);

  const handleClose = () => {
    if (!isLoading) onCloseHandler?.();
  };

  const handleApplyCoupon = async () => {
    if (!estimateItem || !runCheckout || !selectedCoupon) return;
    try {
      setIsApplying(true);
      await runCheckout(estimateItem?.amount || 0, selectedCoupon);
    } finally {
      setIsApplying(false);
    }
  };

  const subTotal = estimateItem?.sub_total ?? 0;
  const totalTax = estimateItem?.taxes?.reduce((acc: number, tax: any) => acc + tax.amount, 0) ?? 0;
  const totalTaxRate =
    estimateItem?.line_item_taxes?.length > 0 ? estimateItem.line_item_taxes[0].tax_rate : 0;
  const discount =
    estimateItem?.line_items?.reduce(
      (acc: number, item: any) => acc + (item.discount_amount ?? 0),
      0
    ) ?? 0;
  const credits = estimateItem?.credits_applied ?? 0;
  const total = estimateItem?.total ?? 0;
  const amountDue = estimateItem?.amount_due ?? 0;

  return (
    <ConfirmDialog
      maxWidth="lg"
      fullWidth
      disableEnforceFocus
      disableAutoFocus
      open={isOpen}
      onClose={handleClose}
      title={t('ADD_BALANCE')}
      content={
        <>
          {(isLoading || isEstimateLoading || !estimateItem) && <AuthLoader />}

          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('ADD_BALANCE_CONFIRMATION', { amount: formatPrice(total) })}
          </Typography>

          {estimateItem && (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 3,
                mx: 'auto',
                my: 2,
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('DESCRIPTION')}</TableCell>
                    <TableCell align="right">{t('AMOUNT')}</TableCell>
                    <TableCell align="right">{t('VAT')}</TableCell>
                    <TableCell align="right">{t('TOTAL')}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {estimateItem?.line_items?.map((item: any) => {
                    const lineTax =
                      estimateItem?.line_item_taxes?.find(
                        (tax: any) => tax.line_item_id === item.id
                      ) ?? {};
                    return (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Typography fontWeight="medium">{item.description}</Typography>
                        </TableCell>
                        <TableCell align="right">{formatPrice(item.amount)}</TableCell>
                        <TableCell align="right">
                          {item?.is_taxed
                            ? `${formatPrice(lineTax?.tax_amount || 0)} (${lineTax?.tax_rate}%)`
                            : formatPrice(0)}
                        </TableCell>
                        <TableCell align="right">
                          {formatPrice(item.amount + (lineTax?.tax_amount || 0))}
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {/* Subtotal */}
                  <TableRow>
                    <TableCell colSpan={3} align="right">
                      {t('SUB_TOTAL')}
                    </TableCell>
                    <TableCell align="right">{formatPrice(subTotal)}</TableCell>
                  </TableRow>

                  {/* VAT */}
                  <TableRow>
                    <TableCell colSpan={3} align="right">
                      {t('VAT')} ({totalTaxRate}%)
                    </TableCell>
                    <TableCell align="right">{formatPrice(totalTax)}</TableCell>
                  </TableRow>

                  {/* Discount */}
                  <TableRow>
                    <TableCell colSpan={3} align="right">
                      {t('DISCOUNT')}
                    </TableCell>
                    <TableCell align="right">{formatPrice(discount)}</TableCell>
                  </TableRow>

                  {/* Total */}
                  <TableRow>
                    <TableCell colSpan={3} align="right">
                      <Typography fontWeight="bold">{t('TOTAL')}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">{formatPrice(total)}</Typography>
                    </TableCell>
                  </TableRow>

                  {/* Credits */}
                  {credits > 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="right">
                        {t('CREDITS_APPLIED')}
                      </TableCell>
                      <TableCell align="right">-{formatPrice(credits)}</TableCell>
                    </TableRow>
                  )}

                  {/* Payable */}
                  <TableRow>
                    <TableCell colSpan={3} align="right">
                      <Typography fontWeight="bold">{t('PAYABLE_AMOUNT')}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">{formatPrice(amountDue)}</Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              {/* Coupon Section */}
              {/* {isSuperAdmin && (
                <Stack direction="row" spacing={2} mt={3} alignItems="center">
                  <Autocomplete
                    sx={{ width: '30%' }}
                    size="small"
                    options={allCoupons}
                    getOptionLabel={(option: any) => option?.code || option?.name || ''}
                    value={selectedCoupon}
                    onChange={(_, newValue) => setSelectedCoupon(newValue)}
                    renderInput={(params) => <TextField {...params} label={t('DISCOUNT_COUPON')} />}
                  />
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleApplyCoupon}
                    disabled={!selectedCoupon || isApplying}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {isApplying ? t('APPLYING') : t('APPLY')}
                  </Button>
                </Stack>
              )} */}
            </Box>
          )}
        </>
      }
      action={
        <Button variant="contained" color="success" onClick={onConfirmHandler} disabled={isLoading}>
          {t('ADD')}
        </Button>
      }
    />
  );
};
